import { AloftPrice, isDefined, isUndefined, schAloftPrice } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectOpinionOfSiteValue } from '../opinionOfSiteValueSelectors.ts';
import { selectAdjustmentSettings, selectCompSequence } from '../orderSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectSiteValue, selectSiteValues } from '../selectSiteValue.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { applyRoundingAndThresholds } from './utils/applyRoundingAndThresholds.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateSiteAdj = (
  opinionOfSiteValue: ReturnType<typeof selectOpinionOfSiteValue>,
  compSiteValue: ReturnType<typeof selectSiteValue>,
) => {
  if (!opinionOfSiteValue || !compSiteValue) return undefined;
  return schAloftPrice.parse(opinionOfSiteValue).sub(compSiteValue).roundToDollarLike();
};

const selectSiteAdj = createPriceSelector(
  [selectOpinionOfSiteValue, selectSiteValue, (_state, id: string) => id],
  calculateSiteAdj,
);

export const selectSiteAdjs = createSelector(
  [selectOpinionOfSiteValue, selectSiteValues, selectCompSequence],
  (opinionOfSiteValue, siteValues, compSequence) =>
    compSequence.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateSiteAdj(opinionOfSiteValue, siteValues[propID]);

      if (isDefined(adj)) acc[propID] = adj;

      return acc;
    }, {}),
);

export const selectSiteAdjsDisplay = createSelector([selectSiteAdjs], (siteAdjs) =>
  Object.fromEntries(Object.entries(siteAdjs).map(([id, adj]) => [id, adj.toString(true)])),
);

const selectAppliedSiteAdj = createPriceSelector(
  [selectSiteAdj, selectAdjustmentSettings],
  (adj, adjSettings) => {
    if (isUndefined(adj)) {
      return undefined;
    }

    return applyRoundingAndThresholds(adj, 'site', adjSettings);
  },
);

export const selectAppliedSiteAdjDisplay = createSelector([selectAppliedSiteAdj], (adj) => {
  if (isUndefined(adj)) {
    return undefined;
  }

  return {
    value: getStringValueFromAdjustment(adj),
    color: getColorFromAdjustment(adj),
  };
});

export const selectAppliedSiteAdjsDisplay = createSelector(
  [selectSiteAdjs, selectAdjustmentSettings, selectManualAdjustmentsEntities],
  (siteAdjs, adjSettings, manualAdjustmentsEntities) =>
    Object.keys(siteAdjs).reduce((acc: Record<string, { value: string; color: string }>, id) => {
      const adj = siteAdjs[id];
      const manualAdjustment = manualAdjustmentsEntities[id]?.site;
      if (isDefined(manualAdjustment)) {
        const manualAdjustmentPrice = schAloftPrice.parse(manualAdjustment);
        acc[id] = {
          value: `${getStringValueFromAdjustment(manualAdjustmentPrice)}*`,
          color: getColorFromAdjustment(manualAdjustmentPrice),
        };
      } else if (isDefined(adj)) {
        acc[id] = {
          value: getStringValueFromAdjustment(applyRoundingAndThresholds(adj, 'site', adjSettings)),
          color: getColorFromAdjustment(adj),
        };
      }

      return acc;
    }, {}),
);
